/** API client for /api/v1/expert/* endpoints. */

import type {
  ExpertConversation,
  ExpertConversationDetail,
  BankerLead,
  LeadListResponse,
  BankerCycleDetail,
  BankerCycleListResponse,
  NotificationHistoryResponse,
} from '../types/expert';
import type {
  Strategy,
  StrategyListItem,
  StrategyCreateRequest,
  StrategyUpdateRequest,
  StrategyParameters,
} from '../types/strategy';

export interface BankerPreferences {
  banker_enabled: boolean;
  banker_sectors: string[] | null;
  banker_exchanges: string[] | null;
  heatmap_exchanges: string[] | null;
  banker_max_leads_per_cycle: number;
  banker_max_daily_notifications: number;
  banker_lead_archive_days: number;
  banker_analyst_name: string;
  banker_advisor_name: string;
  banker_analyst_voice: string | null;
  banker_advisor_voice: string | null;
  banker_voice_autoplay: boolean;
  /** Whether the 2D avatar overlay is shown (Story 7.4) */
  banker_avatar_enabled: boolean;
  /** Chart provider preference: 'internal' (default) or 'tradingview' (v30-1-6) */
  chart_provider?: 'internal' | 'tradingview';
  // Push notification webhooks (per-user, Pro + Premium)
  teams_webhook_url: string | null;
  teams_notify_leads: boolean;
  teams_notify_alerts: boolean;
  discord_webhook_url: string | null;
  discord_notify_leads: boolean;
  discord_notify_alerts: boolean;
  // Per-channel URLs (override shared URL when set)
  discord_leads_webhook_url: string | null;
  discord_alerts_webhook_url: string | null;
  teams_leads_webhook_url: string | null;
  teams_alerts_webhook_url: string | null;
}

export interface TTSVoice {
  id: string;
  name: string;
  preview_url?: string;
}

export interface Exchange {
  code: string;
  name: string;
  country: string;
  currency: string;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

class ExpertApiService {
  private getToken(): string | null {
    return localStorage.getItem('token');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = {};

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
      throw new Error('Session expired');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'Request failed');
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  // ─── Banker Leads ──────────────────────────────────────────────

  async getBankerLeads(params: {
    status?: string;
    source?: string;
    lead_type?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<LeadListResponse> {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.source) qs.set('source', params.source);
    if (params.lead_type) qs.set('lead_type', params.lead_type);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.offset) qs.set('offset', String(params.offset));
    const query = qs.toString() ? `?${qs}` : '';
    return this.request<LeadListResponse>(`/api/v1/banker/leads${query}`);
  }

  async getBankerLead(id: number): Promise<BankerLead> {
    return this.request<BankerLead>(`/api/v1/banker/leads/${id}`);
  }

  async markLeadActedOn(id: number): Promise<{ id: number; status: string }> {
    return this.request<{ id: number; status: string }>(`/api/v1/banker/leads/${id}/acted`, {
      method: 'PATCH',
    });
  }

  async getBankerNotifications(params: {
    date_range?: string;
    priority?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<NotificationHistoryResponse> {
    const qs = new URLSearchParams();
    if (params.date_range) qs.set('date_range', params.date_range);
    if (params.priority) qs.set('priority', params.priority);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.offset) qs.set('offset', String(params.offset));
    const query = qs.toString() ? `?${qs}` : '';
    return this.request<NotificationHistoryResponse>(`/api/v1/banker/notifications${query}`);
  }

  // ─── Conversations ──────────────────────────────────────────────

  async getConversations(
    params: { limit?: number; offset?: number } = {}
  ): Promise<ExpertConversation[]> {
    const qs = new URLSearchParams();
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.offset) qs.set('offset', String(params.offset));
    const query = qs.toString() ? `?${qs}` : '';
    return this.request<ExpertConversation[]>(`/api/v1/expert/conversations${query}`);
  }

  async getConversation(id: number): Promise<ExpertConversationDetail> {
    return this.request<ExpertConversationDetail>(`/api/v1/expert/conversations/${id}`);
  }

  async deleteConversation(id: number): Promise<void> {
    return this.request<void>(`/api/v1/expert/conversations/${id}`, {
      method: 'DELETE',
    });
  }

  // ─── Strategies ────────────────────────────────────────────────

  async getStrategies(params: {
    type?: string;
    status?: string;
    is_active?: boolean;
    limit?: number;
    offset?: number;
  } = {}): Promise<StrategyListItem[]> {
    const qs = new URLSearchParams();
    if (params.type) qs.set('type', params.type);
    if (params.status) qs.set('status', params.status);
    if (params.is_active !== undefined) qs.set('is_active', String(params.is_active));
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.offset) qs.set('offset', String(params.offset));
    const query = qs.toString() ? `?${qs}` : '';
    return this.request<StrategyListItem[]>(`/api/v1/expert/strategies${query}`);
  }

  async getStrategy(id: number): Promise<Strategy> {
    return this.request<Strategy>(`/api/v1/expert/strategies/${id}`);
  }

  async createStrategy(data: StrategyCreateRequest): Promise<Strategy> {
    return this.request<Strategy>('/api/v1/expert/strategies', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateStrategy(id: number, data: StrategyUpdateRequest): Promise<Strategy> {
    return this.request<Strategy>(`/api/v1/expert/strategies/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteStrategy(id: number): Promise<void> {
    return this.request<void>(`/api/v1/expert/strategies/${id}`, {
      method: 'DELETE',
    });
  }

  async activateStrategy(id: number, isActive: boolean): Promise<Strategy> {
    return this.request<Strategy>(`/api/v1/expert/strategies/${id}/activate`, {
      method: 'POST',
      body: JSON.stringify({ is_active: isActive }),
    });
  }

  async toggleUserActivation(id: number, isActive: boolean): Promise<Strategy> {
    return this.request<Strategy>(`/api/v1/expert/strategies/${id}/user-activate`, {
      method: 'POST',
      body: JSON.stringify({ is_active: isActive }),
    });
  }

  async promoteStrategy(id: number): Promise<Strategy> {
    return this.request<Strategy>(`/api/v1/expert/strategies/${id}/promote`, {
      method: 'POST',
    });
  }

  async extractStrategy(id: number): Promise<Strategy> {
    return this.request<Strategy>(`/api/v1/expert/strategies/${id}/extract`, {
      method: 'POST',
    });
  }

  async suggestParameters(id: number): Promise<{ parameters: StrategyParameters }> {
    return this.request<{ parameters: StrategyParameters }>(
      `/api/v1/expert/strategies/${id}/suggest-parameters`,
      { method: 'POST' },
    );
  }

  // ─── Banker Preferences ────────────────────────────────────────

  async getBankerPreferences(): Promise<BankerPreferences> {
    return this.request<BankerPreferences>('/api/v1/expert/banker/preferences');
  }

  async updateBankerPreferences(data: Partial<BankerPreferences>): Promise<BankerPreferences> {
    return this.request<BankerPreferences>('/api/v1/expert/banker/preferences', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getExchanges(search?: string): Promise<Exchange[]> {
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.request<Exchange[]>(`/api/v1/expert/exchanges${qs}`);
  }

  // ─── Banker Cycles ───────────────────────────────────────────────

  async getBankerCycles(params: {
    days?: number;
    status?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<BankerCycleListResponse> {
    const qs = new URLSearchParams();
    if (params.days) qs.set('days', String(params.days));
    if (params.status) qs.set('status', params.status);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.offset) qs.set('offset', String(params.offset));
    const query = qs.toString() ? `?${qs}` : '';
    return this.request<BankerCycleListResponse>(`/api/v1/banker/cycles${query}`);
  }

  async getBankerCycle(id: number): Promise<BankerCycleDetail> {
    return this.request<BankerCycleDetail>(`/api/v1/banker/cycles/${id}`);
  }

  // ─── TTS (Story 7.1) ──────────────────────────────────────────────

  async ttsSpeak(text: string, voice?: string, speed?: number): Promise<Response> {
    const token = this.getToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${API_URL}/api/v1/expert/tts`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ text, voice, speed }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'TTS failed' }));
      throw new Error(error.detail || 'TTS request failed');
    }
    return response;
  }

  async getTTSVoices(): Promise<TTSVoice[]> {
    return this.request<TTSVoice[]>('/api/v1/expert/tts/voices');
  }

  // ─── STT — Whisper fallback (Story 7.3) ─────────────────────────

  /**
   * Upload an audio Blob to the backend and receive a Whisper transcription.
   * Used as fallback for Firefox (no Web Speech API).
   */
  async sttTranscribe(audioBlob: Blob, filename = 'recording.webm'): Promise<{ text: string; language: string }> {
    const token = this.getToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const form = new FormData();
    form.append('audio', audioBlob, filename);

    const response = await fetch(`${API_URL}/api/v1/expert/stt`, {
      method: 'POST',
      headers,
      body: form,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'STT failed' }));
      throw new Error(error.detail || 'STT request failed');
    }

    return response.json() as Promise<{ text: string; language: string }>;
  }

  // ─── SSE Chat Stream ────────────────────────────────────────────

  /**
   * Send a message and return the raw Response for SSE streaming.
   * Caller is responsible for reading the ReadableStream.
   */
  async sendMessageStream(
    message: string,
    conversationId?: number,
    signal?: AbortSignal,
    personaSlug?: string,
  ): Promise<Response> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}/api/v1/expert/chat`, {
      method: 'POST',
      headers,
      signal,
      body: JSON.stringify({
        message,
        conversation_id: conversationId ?? null,
        persona_slug: personaSlug ?? 'analyst',
      }),
    });

    if (response.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
      throw new Error('Session expired');
    }

    if (response.status === 409) {
      throw new Error('Conversation has an active stream. Please wait for the current response.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'Request failed');
    }

    return response;
  }
}

export const expertApi = new ExpertApiService();
